import { Checkbox } from '@sparefoot/react-core';
import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { on, off } from '@sparefoot/react-core/utils/dom';
import { Link } from 'components/core/Link';
import CaretDown from 'components/svg/CaretDown';
import { compose } from 'recompose';
import { withShowHide } from '@sparefoot/react-core/decorators';
import { connect } from 'react-redux';
import { breakpointsByName } from 'config/breakpoints';
import { COLOR_BLUE, COLOR_SECONDARY_BLUE } from 'config/colors';
import GeoSpecificMenu from './GeoSpecificMenu/GeoSpecificMenu';

const mapStateToProps = (state) => {
	const pagesNearbyCities = state.pages?.content?.nearbyCities;
	const appNearbyCities = state.app?.nearbyCities;
	const resolvedNearbyCities = pagesNearbyCities || appNearbyCities || [];
	const drillDownLinks =
		state.search?.drillDownLinks ??
		state.facilities?.drillDownLinks ??
		null;
	return {
		nearbyCities: resolvedNearbyCities,
		drillDownLinks,
	};
};

export const enhance = compose(
	connect(mapStateToProps),
	withShowHide('findStorage'),
	withShowHide('dropdown'),
	withShowHide('owners'),
);

/**
 * Returns a safe href for use in Link components.
 * Only allows root-relative paths or https: URLs to prevent javascript:, data:, or protocol-relative URLs.
 *
 * Security behavior:
 * - javascript:, data:, and other non-https protocols are not allowed as schemes; strings that look
 *   like them are treated as path segments and normalized with a leading slash (e.g. "/javascript:...").
 * - Protocol-relative URLs (e.g. "//evil.com") are rejected and return '#'.
 * - Paths without a leading slash (e.g. "self-storage/texas/del-valle/") are normalized to
 *   root-relative by prepending '/'. This is intentional so that backend nearbyCities.uri values
 *   that omit the leading slash still produce navigable links; if the backend only ever sends
 *   paths with leading slashes, unexpected bare paths could be treated as invalid and return '#'
 *   for a stricter fail-safe policy.
 *
 * @param {string} uri - A path (e.g. "/self-storage/texas/del-valle/" or "self-storage/texas/del-valle/") or https URL.
 * @returns {string} The URI if safe; otherwise '#'.
 */
export const getSafeHref = (uri) => {
	if (typeof uri !== 'string') {
		return '#';
	}
	const trimmed = uri.trim();
	if (!trimmed) {
		return '#';
	}
	if (trimmed.toLowerCase().startsWith('https://')) {
		return trimmed;
	}
	// Reject protocol-relative URLs (e.g. "//evil.com") which could load external content
	if (trimmed.startsWith('//')) {
		return '#';
	}
	if (trimmed.startsWith('/')) {
		return trimmed;
	}
	// Path without leading slash – normalize to root-relative
	return `/${trimmed}`;
};

export const getFindStorageNearUserLinks = (nearbyCities = []) => {
	return nearbyCities.slice(0, 5).map(({ city, state, uri }) => ({
		label: `Self Storage in ${city}, ${state}`,
		href: getSafeHref(uri),
	}));
};

export const isDrillDownLinksPresent = (drillDownLinks) => {
	if (!drillDownLinks) {
		return false;
	}
	return Object.keys(drillDownLinks).some(
		(key) => drillDownLinks[key] && drillDownLinks[key].length > 0,
	);
};

const FIND_STORAGE_TOP_CITIES_LINKS = [
	{
		label: 'Self Storage in Atlanta, GA',
		href: '/self-storage/georgia/atlanta/',
	},
	{
		label: 'Self Storage in Boston, MA',
		href: '/self-storage/massachusetts/boston/',
	},
	{
		label: 'Self Storage in Dallas, TX',
		href: '/self-storage/texas/dallas/',
	},
	{
		label: 'Self Storage in Denver, CO',
		href: '/self-storage/colorado/denver/',
	},
	{
		label: 'Self Storage in Las Vegas, NV',
		href: '/self-storage/nevada/las-vegas/',
	},
	{
		label: 'Self Storage in Louisville, KY',
		href: '/self-storage/kentucky/louisville/',
	},
	{
		label: 'Self Storage in Los Angeles, CA',
		href: '/self-storage/california/los-angeles/',
	},
	{
		label: 'Self Storage in Miami, FL',
		href: '/self-storage/florida/miami/',
	},
	{
		label: 'Self Storage in New York, NY',
		href: '/self-storage/new-york/new-york/',
	},
	{
		label: 'Self Storage in Seattle, WA',
		href: '/self-storage/washington/seattle/',
	},
];

export class Menu extends PureComponent {
	constructor(props) {
		super(props);
		this.handleClickOutside = this.handleClickOutside.bind(this);
		this.handleFindStorageToggle = this.handleFindStorageToggle.bind(this);
		this.handleStorageTypesToggle =
			this.handleStorageTypesToggle.bind(this);
		this.handleOwnersToggle = this.handleOwnersToggle.bind(this);
	}

	isDesktopViewport() {
		return (
			typeof window !== 'undefined' &&
			window.innerWidth >= breakpointsByName.spMedium
		);
	}

	closeOtherDropdowns(excludedDropdown) {
		const { onSetFindStorage, onSetDropdown, onSetOwners } = this.props;

		if (excludedDropdown !== 'findStorage') {
			onSetFindStorage(false);
		}

		if (excludedDropdown !== 'storageTypes') {
			onSetDropdown(false);
		}

		if (excludedDropdown !== 'owners') {
			onSetOwners(false);
		}
	}

	handleFindStorageToggle(event) {
		const { findStorageShowing, onToggleFindStorage } = this.props;

		if (this.isDesktopViewport() && !findStorageShowing) {
			this.closeOtherDropdowns('findStorage');
		}

		onToggleFindStorage(event);
	}

	handleStorageTypesToggle(event) {
		const { dropdownShowing, onToggleDropdown } = this.props;

		if (this.isDesktopViewport() && !dropdownShowing) {
			this.closeOtherDropdowns('storageTypes');
		}

		onToggleDropdown(event);
	}

	handleOwnersToggle(event) {
		const { ownersShowing, onToggleOwners } = this.props;

		if (this.isDesktopViewport() && !ownersShowing) {
			this.closeOtherDropdowns('owners');
		}

		onToggleOwners(event);
	}

	componentDidMount() {
		on('mouseup', this.handleClickOutside);
	}

	componentWillUnmount() {
		off('mouseup', this.handleClickOutside);
	}

	handleClickOutside(event) {
		const clickedElement = event.target;
		const isMenuOrToggleClick =
			!!clickedElement &&
			typeof clickedElement.closest === 'function' &&
			(!!clickedElement.closest('.nav-menu') ||
				!!clickedElement.closest('.nav-button') ||
				!!clickedElement.closest('.dropdown-toggle'));

		if (!isMenuOrToggleClick) {
			const {
				onSetFindStorage,
				onSetDropdown,
				onSetOwners,
				onCloseNavigation,
			} = this.props;
			onSetFindStorage(false);
			onSetDropdown(false);
			onSetOwners(false);
			if (onCloseNavigation) {
				onCloseNavigation();
			}
		}
	}

	render() {
		const {
			findStorageShowing,
			dropdownShowing,
			ownersShowing,
			nearbyCities,
			drillDownLinks,
		} = this.props;

		const findStorageNearUserLinks =
			getFindStorageNearUserLinks(nearbyCities);

		const isDrillDownLinksAvailable =
			isDrillDownLinksPresent(drillDownLinks);

		return (
			<div className="nav-menu">
				<ul className="menu-list">
					<li className="menu-item">
						{/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
						<label
							className={classnames(
								'menu-link dropdown-toggle ss-link',
								{ open: findStorageShowing },
							)}
							htmlFor="find-storage-checkbox"
							aria-expanded={findStorageShowing}
							aria-haspopup="true"
						>
							<Checkbox
								onChange={this.handleFindStorageToggle}
								key="Find Storage Dropdown"
								id="find-storage-checkbox"
								name="find-storage-checkbox"
								className="visually-hidden"
								checked={findStorageShowing}
							/>
							Find Storage{' '}
							<CaretDown
								fill={
									findStorageShowing
										? COLOR_SECONDARY_BLUE
										: COLOR_BLUE
								}
							/>
						</label>
						<div
							className={classnames(
								'find-storage-dropdown-menu',
								{
									showing: findStorageShowing,
								},
							)}
						>
							<div className="find-storage-dropdown-content">
								<div className="find-storage-columns">
									{drillDownLinks &&
									isDrillDownLinksAvailable ? (
										<GeoSpecificMenu
											geoLocation={drillDownLinks}
										/>
									) : (
										<>
											{nearbyCities?.length > 0 && (
												<div className="find-storage-column">
													<p className="find-storage-heading">
														Self Storage Near
														You:
													</p>
													{findStorageNearUserLinks.map(
														(cityLink) => (
															<Link
																key={
																	cityLink.label
																}
																className="menu-link find-storage-link"
																href={
																	cityLink.href
																}
																rel="noopener noreferrer"
															>
																{
																	cityLink.label
																}
															</Link>
														),
													)}
												</div>
											)}

											<div className="find-storage-column">
												<p className="find-storage-heading">
													Self Storage in Top
													Cities:
												</p>
												{FIND_STORAGE_TOP_CITIES_LINKS.map(
													(cityLink) => (
														<Link
															key={
																cityLink.label
															}
															className="menu-link find-storage-link"
															href={
																cityLink.href
															}
															rel="noopener noreferrer"
														>
															{cityLink.label}
														</Link>
													),
												)}
											</div>
										</>
									)}
								</div>
							</div>
							<div className="find-storage-cta-row find-storage-cta-row--desktop">
								<Link
									className="menu-link find-storage-cta"
									href="/storage-units-near-me"
									rel="noopener noreferrer"
								>
									Browse All Facilities Near Me
								</Link>
							</div>
						</div>
					</li>

					<li className="menu-item">
						{/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
						<label
							className={classnames(
								'menu-link dropdown-toggle ss-link',
								{ open: dropdownShowing },
							)}
							htmlFor="storage-types-checkbox"
							aria-expanded={dropdownShowing}
							aria-haspopup="true"
						>
							<Checkbox
								onChange={this.handleStorageTypesToggle}
								key="Storage Types Dropdown"
								id="storage-types-checkbox"
								name="storage-types-checkbox"
								className="visually-hidden"
								checked={dropdownShowing}
							/>
							Storage Types{' '}
							<CaretDown
								fill={
									dropdownShowing
										? COLOR_SECONDARY_BLUE
										: COLOR_BLUE
								}
							/>
						</label>
						<div>
							<ul
								className={classnames('dropdown-menu', {
									showing: dropdownShowing,
								})}
							>
								<li className="menu-item">
									<Link
										className="menu-link"
										href="/storage-units-near-me"
										rel="noopener noreferrer"
									>
										Find self-storage
									</Link>
								</li>
								<li className="menu-item">
									<Link
										className="menu-link"
										href="/storage-types/vehicle-self-storage"
										rel="noopener noreferrer"
									>
										Vehicle Storage
									</Link>
								</li>
								<li className="menu-item">
									<Link
										className="menu-link"
										href="/storage-types/climate-controlled-self-storage"
										rel="noopener noreferrer"
									>
										Climate Controlled Storage
									</Link>
								</li>
							</ul>
						</div>
					</li>
					<li className="menu-item">
						<Link
							className="menu-link"
							href="/what-to-expect"
							rel="noopener noreferrer"
						>
							Size Guide
						</Link>
					</li>
					<li className="menu-item">
						<Link
							className="menu-link"
							href="https://www.selfstorage.com/blog/"
							rel="noopener noreferrer"
						>
							Blog
						</Link>
					</li>
					<li className="menu-item hide-on-desktop">
						{/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
						<label
							className={classnames(
								'menu-link dropdown-toggle owners-dropdown-menu ss-link',
								{ open: ownersShowing },
							)}
							htmlFor="owners-checkbox-mobile"
							aria-expanded={ownersShowing}
							aria-haspopup="true"
						>
							<Checkbox
								onChange={this.handleOwnersToggle}
								key="Owners Dropdown"
								id="owners-checkbox-mobile"
								name="owners-checkbox-mobile"
								className="visually-hidden"
								checked={ownersShowing}
							/>
							Owners{' '}
							<CaretDown
								fill={
									ownersShowing
										? COLOR_SECONDARY_BLUE
										: COLOR_BLUE
								}
							/>
						</label>
						<div>
							<ul
								className={classnames('dropdown-menu', {
									showing: ownersShowing,
								})}
							>
								<li className="menu-item">
									<Link
										className="menu-link"
										href="https://www.storable.com/products/marketplace/"
										rel="noopener noreferrer"
									>
										Add Your Facility
									</Link>
								</li>
								<li className="menu-item">
									<Link
										className="menu-link"
										href="https://argus-selfstorage.com/"
										rel="noopener noreferrer"
									>
										Buy & Sell Facilities
									</Link>
								</li>
							</ul>
						</div>
					</li>
					<li className="menu-item find-storage-mobile-cta-row">
						<Link
							className="menu-link find-storage-cta"
							href="/storage-units-near-me"
							rel="noopener noreferrer"
						>
							Browse All Facilities Near Me
						</Link>
					</li>
				</ul>
			</div>
		);
	}
}

Menu.propTypes = {
	onToggleFindStorage: PropTypes.func.isRequired,
	onToggleDropdown: PropTypes.func.isRequired,
	onToggleOwners: PropTypes.func.isRequired,
	findStorageShowing: PropTypes.bool,
	dropdownShowing: PropTypes.bool,
	ownersShowing: PropTypes.bool,
	onSetFindStorage: PropTypes.func.isRequired,
	onSetDropdown: PropTypes.func.isRequired,
	onSetOwners: PropTypes.func.isRequired,
	onCloseNavigation: PropTypes.func,
	nearbyCities: PropTypes.arrayOf(PropTypes.object),
	drillDownLinks: PropTypes.object,
};

export default enhance(Menu);
